import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@vd/ui';
import { Check, Code2, Copy, Monitor, MousePointer2, Sparkles, Terminal, type LucideIcon } from 'lucide-react';
import { useCopyFeedback } from '../lib/useCopyFeedback';
import { EVERY_SCREEN, MCP_SETUP_SCREEN } from '../lib/screenNames';
import styles from './McpSetupPage.module.css';
import { useTabTitle, useTabWorkspaceName } from '../lib/tabTitle';
import { api } from '../lib/api';
import { useAuth } from '../features/auth/AuthProvider';
import { AgentConnectionCheck } from '../features/mcp/AgentConnectionCheck';

/**
 * MCP Connector setup page. One tabbed picker per client because each
 * client has its own config shape and auth story:
 *
 *   - Claude Code: one-line CLI command, OAuth via the CLI itself (DCR)
 *   - Claude Desktop: native remote-MCP support via Settings → Connectors
 *     → Add custom connector; OAuth (DCR) handled by the app. The legacy
 *     mcp-remote bridge config is no longer needed in current builds.
 *   - Grok (xAI): grok.com/connectors → New Connector → Custom, with the
 *     live address, always: Grok reaches only public servers (TOADO-908). Its
 *     sign-in is OAuth on Toado's side (DCR accepts https callbacks for
 *     hosted clients); a token is the fallback.
 *   - Cursor: native add from the Customize page (Cursor Settings → MCP
 *     in older builds), OAuth (DCR) handled
 *     by the editor. JSON config is shown as the file-based alternative.
 *   - Codex: ~/.codex/config.toml with url, then `codex mcp login toado`
 *     to do the OAuth flow. PAT path stays available via http_headers.
 *   - Other (Cline, Continue, custom): universal HTTP + Authorization
 *     header pattern that any compliant client can use
 *
 * needsPat auto-shows the token-generation step only for the Other tab.
 * The named clients all do OAuth natively now.
 */

const PROD_MCP_URL = 'https://mcp.toado.dev';
const DEV_MCP_URL = 'http://localhost:3002';

function currentMcpUrl(): string {
  if (typeof window === 'undefined') return PROD_MCP_URL;
  return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
    ? DEV_MCP_URL
    : PROD_MCP_URL;
}

type ClientId = 'claude-code' | 'claude-desktop' | 'grok' | 'cursor' | 'codex' | 'other';

/** Each client with an icon for what it is, Other with none (Jason, 24 Oct, t-33IDmq). */
const CLIENT_TABS: Array<{ id: ClientId; label: string; icon?: LucideIcon }> = [
  { id: 'claude-code', label: 'Claude Code', icon: Terminal },
  { id: 'claude-desktop', label: 'Claude Desktop', icon: Monitor },
  { id: 'grok', label: 'Grok', icon: Sparkles },
  { id: 'cursor', label: 'Cursor', icon: MousePointer2 },
  { id: 'codex', label: 'Codex', icon: Code2 },
  { id: 'other', label: 'Other' },
];

/** Copied only when the clipboard took it, as the check says it (TOADO-974 review round 7). */
function CopyBlock({ label, value }: { label: string; value: string }) {
  const [copied, copy] = useCopyFeedback();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <pre className={styles.config}>{value}</pre>
      <Button
        variant="secondary"
        size="sm"
        leftIcon={copied === 'yes' ? <Check size={14} /> : <Copy size={14} />}
        onClick={() => { void copy(value); }}
      >
        {copied === 'yes' ? 'Copied' : label}
      </Button>
      {copied === 'failed' && <span className={styles.problem} role="status">Could not copy: select the text above and copy it yourself.</span>}
    </div>
  );
}

function ClientPanel({ client, mcpUrl }: { client: ClientId; mcpUrl: string }) {
  const url = `${mcpUrl}/mcp`;

  if (client === 'claude-code') {
    return (
      <>
        <p style={{ color: 'var(--text-secondary)', fontSize: 13, margin: '4px 0 12px' }}>
          Two steps: register the server with the CLI, then authenticate from inside a
          Claude Code session. The consent screen (with three access levels:{' '}
          <em>Read-only</em>, <em>Read + comment</em>, or <em>Full access</em>) appears
          at the second step.
        </p>

        <p style={{ color: 'var(--text-secondary)', fontSize: 13, margin: '4px 0 8px' }}>
          <strong>1. Register the server.</strong> Run this from any terminal. The{' '}
          <code>--scope user</code> flag makes Toado available across all your projects,
          not just the one you're in.
        </p>
        <CopyBlock
          label="Copy command"
          value={`claude mcp add --transport http --scope user toado ${url}`}
        />

        <p style={{ color: 'var(--text-secondary)', fontSize: 13, margin: '14px 0 8px' }}>
          <strong>2. Authenticate.</strong> Open a Claude Code session and run{' '}
          <code>/mcp</code>. A browser tab opens for OAuth approval; pick your scope and
          approve. The token is stored automatically. From a terminal,{' '}
          <code>claude mcp login toado</code> does the same.
        </p>
        <ul style={{ color: 'var(--text-secondary)', fontSize: 13, paddingLeft: 18, lineHeight: 1.7, margin: '0 0 12px' }}>
          <li>
            <strong>VS Code or Cursor:</strong> open the Claude Code chat panel and type{' '}
            <code>/mcp</code> in the prompt.
          </li>
          <li>
            <strong>Terminal:</strong> run <code>claude</code> in any directory, then type{' '}
            <code>/mcp</code> at the prompt.
          </li>
        </ul>

        <p style={{ color: 'var(--text-tertiary)', fontSize: 12, marginTop: 10 }}>
          Prefer a token? Generate one below and run{' '}
          <code>claude mcp add --transport http --scope user toado {url} --header "Authorization: Bearer &lt;your-token&gt;"</code>,
          which skips the <code>/mcp</code> auth step.
        </p>
      </>
    );
  }

  if (client === 'claude-desktop') {
    return (
      <>
        <p style={{ color: 'var(--text-secondary)', fontSize: 13, margin: '4px 0 12px' }}>
          Claude Desktop has native support for remote MCP servers. OAuth is handled by the app
          on first connect.
        </p>
        <ol style={{ color: 'var(--text-secondary)', fontSize: 14, paddingLeft: 18, lineHeight: 1.7, margin: '0 0 12px' }}>
          <li>Open Claude Desktop → <strong>Settings</strong> → <strong>Connectors</strong></li>
          <li>Click <strong>Add custom connector</strong></li>
          <li>Paste the URL below and click Add. A browser tab opens for OAuth approval.</li>
        </ol>
        <CopyBlock label="Copy URL" value={url} />
        <p style={{ color: 'var(--text-tertiary)', fontSize: 12, marginTop: 10 }}>
          See{' '}
          <a
            href="https://support.claude.com/en/articles/11175166-getting-started-with-custom-connectors-using-remote-mcp"
            target="_blank"
            rel="noreferrer"
            style={{ color: 'var(--hl-blue)' }}
          >
            Anthropic's custom-connector guide
          </a>{' '}
          for screenshots. Older Claude Desktop versions used a JSON config + the{' '}
          <code>mcp-remote</code> bridge, which is no longer needed if your app is current.
        </p>
      </>
    );
  }

  if (client === 'grok') {
    // Grok runs in xAI's cloud and reaches only servers on the public
    // internet (docs.x.ai/grok/connectors), so it always gets the live
    // address, whichever stack this page is served from (Jason, 22 Oct).
    return (
      <>
        <p style={{ color: 'var(--text-secondary)', fontSize: 13, margin: '4px 0 12px' }}>
          Grok has native support for remote MCP servers. You sign in to Toado and approve on
          first connect.
        </p>
        <ol style={{ color: 'var(--text-secondary)', fontSize: 14, paddingLeft: 18, lineHeight: 1.7, margin: '0 0 12px' }}>
          <li>Open <strong>grok.com/connectors</strong> (or the connectors settings in the Grok app)</li>
          <li>Click <strong>New Connector</strong>, then <strong>Custom</strong></li>
          <li>Paste the URL below and click Add. Sign in to Toado and approve when Grok asks.</li>
        </ol>
        <CopyBlock label="Copy URL" value={`${PROD_MCP_URL}/mcp`} />
        <p style={{ color: 'var(--text-tertiary)', fontSize: 12, marginTop: 10 }}>
          See{' '}
          <a href="https://docs.x.ai/grok/connectors" target="_blank" rel="noreferrer" style={{ color: 'var(--hl-blue)' }}>
            xAI's connector guide
          </a>{' '}
          for screenshots. If Grok asks for a token rather than a sign-in, generate one in{' '}
          <a href="/me/settings/api-tokens" style={{ color: 'var(--hl-blue)' }}>API tokens</a>{' '}
          and give it as the bearer token.
        </p>
      </>
    );
  }

  if (client === 'cursor') {
    return (
      <>
        <p style={{ color: 'var(--text-secondary)', fontSize: 13, margin: '4px 0 12px' }}>
          Cursor supports remote MCP servers with OAuth (dynamic registration), so no token is
          needed. The fastest path is the in-app UI:
        </p>
        <ol style={{ color: 'var(--text-secondary)', fontSize: 14, paddingLeft: 18, lineHeight: 1.7, margin: '0 0 12px' }}>
          <li>Open <strong>Customize</strong> in Cursor&rsquo;s sidebar (older versions: <strong>Cursor
            Settings</strong> → <strong>MCP</strong>)</li>
          <li>Add a new MCP server, paste the URL below, and save</li>
          <li>Approve in the browser tab Cursor opens for OAuth</li>
        </ol>
        <CopyBlock label="Copy URL" value={url} />
        <p style={{ color: 'var(--text-tertiary)', fontSize: 12, marginTop: 10 }}>
          Prefer to edit a config file? Add this to <code>~/.cursor/mcp.json</code> (or
          per-project <code>.cursor/mcp.json</code>):
        </p>
        <CopyBlock
          label="Copy config"
          value={`{
  "mcpServers": {
    "toado": {
      "url": "${url}"
    }
  }
}`}
        />
      </>
    );
  }

  if (client === 'codex') {
    return (
      <>
        <p style={{ color: 'var(--text-secondary)', fontSize: 13, margin: '4px 0 12px' }}>
          OpenAI's Codex CLI supports OAuth for remote MCP servers. Two steps: add the server to
          your config, then run the login command.
        </p>
        <p style={{ color: 'var(--text-secondary)', fontSize: 13, margin: '4px 0 8px' }}>
          1. Add this block to <code>~/.codex/config.toml</code> (or project-scoped{' '}
          <code>.codex/config.toml</code>):
        </p>
        <CopyBlock
          label="Copy config"
          value={`[mcp_servers.toado]
url = "${url}"`}
        />
        <p style={{ color: 'var(--text-secondary)', fontSize: 13, margin: '12px 0 8px' }}>
          2. Run the OAuth login command:
        </p>
        <CopyBlock label="Copy command" value="codex mcp login toado" />
        <p style={{ color: 'var(--text-tertiary)', fontSize: 12, marginTop: 10 }}>
          Step 1 as one command instead: <code>codex mcp add toado --url {url}</code>.
        </p>
        <p style={{ color: 'var(--text-tertiary)', fontSize: 12, marginTop: 10 }}>
          Prefer a token? Generate one below and use{' '}
          <code>http_headers = {`{ Authorization = "Bearer <token>" }`}</code> in the TOML block
          (skip the login step). Or keep it out of the file with{' '}
          <code>bearer_token_env_var = "TOADO_TOKEN"</code>.
        </p>
      </>
    );
  }

  // 'other'
  return (
    <>
      <p style={{ color: 'var(--text-secondary)', fontSize: 13, margin: '4px 0 12px' }}>
        Any MCP client with HTTP transport + custom auth headers can connect. This covers Cline,
        Continue, Goose, Zed, and anything you build with the MCP SDK.
      </p>
      <p style={{ color: 'var(--text-secondary)', fontSize: 13, margin: '4px 0 8px' }}>
        Generate a token below, then point your client at:
      </p>
      <CopyBlock label="Copy URL" value={url} />
      <p style={{ color: 'var(--text-secondary)', fontSize: 13, margin: '12px 0 8px' }}>
        With this header on every request:
      </p>
      <CopyBlock label="Copy header" value={`Authorization: Bearer <your-token>`} />
      <p style={{ color: 'var(--text-tertiary)', fontSize: 12, marginTop: 10 }}>
        <strong>Cline:</strong> Settings → MCP Servers → Add Remote (HTTP).{' '}
        <strong>Continue:</strong> edit <code>~/.continue/config.yaml</code> (or workspace
        equivalent) and add a <code>contextProviders</code>/<code>mcpServers</code> entry with the
        URL and headers above.
      </p>
    </>
  );
}

/**
 * Just the MCP-setup body — pickable client tabs, optional PAT step,
 * try-it block, troubleshooting list. Renders WITHOUT the outer
 * `.page` wrapper so it can also be embedded inside a Modal (e.g. on
 * the HomePage onboarding dashboard) where page-level padding +
 * max-width would compete with the modal's own container styling.
 * `McpSetupPage` thin-wraps this with the page chrome.
 */
export function McpSetupContent() {
  const mcpUrl = currentMcpUrl();
  const [activeClient, setActiveClient] = useState<ClientId>('claude-code');
  // Only Other uses a PAT as the primary path. The named clients
  // (Claude Code, Claude Desktop, Grok, Cursor, Codex) all handle OAuth
  // natively, so we skip the token-generation step for them.
  const needsPat = activeClient === 'other';
  /** Each client is a screen for pins (TOADO-701): its panel, and the token step only Other has. */
  const clientScreen = `${MCP_SETUP_SCREEN}/${activeClient}`;
  // The workspace's projects, for the check (TOADO-974).
  const { currentCompanyId } = useAuth();
  const projectsQuery = useQuery({
    queryKey: ['projects', currentCompanyId],
    queryFn: () => api.listProjects(currentCompanyId!),
    enabled: !!currentCompanyId,
  });
  const projects = projectsQuery.data ?? [];
  const nextStep = needsPat ? 3 : 2;

  return (
    <>
      <h1 className={styles.h1}>MCP Connector</h1>
      <p className={styles.sub}>
        Connect any MCP-capable client to read your tickets, post comments, and move tickets
        through columns. All access is scoped to whatever permissions you grant.
      </p>

      <section className={styles.step}>
        <div className={styles.stepNum}>1</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3 className={styles.stepTitle}>Pick your client</h3>
          <div className={styles.tabs} role="tablist" aria-label="MCP client" data-tour-anchor="mcp.clients">
            {CLIENT_TABS.map((t) => (
              <button
                key={t.id}
                role="tab"
                aria-selected={activeClient === t.id}
                className={activeClient === t.id ? `${styles.tab} ${styles.tabActive}` : styles.tab}
                onClick={() => setActiveClient(t.id)}
              >
                {t.icon && <t.icon size={14} aria-hidden="true" />}
                {t.label}
              </button>
            ))}
          </div>
          <div className={styles.tabPanel} role="tabpanel" data-toado-view={clientScreen} data-tour-anchor="mcp.client-panel">
            <ClientPanel client={activeClient} mcpUrl={mcpUrl} />
          </div>
        </div>
      </section>

      {needsPat && (
        <section className={styles.step} data-toado-view={clientScreen}>
          <div className={styles.stepNum}>2</div>
          <div style={{ flex: 1 }}>
            <h3 className={styles.stepTitle}>Generate a token</h3>
            <p style={{ color: 'var(--text-secondary)', fontSize: 13, margin: '4px 0 12px' }}>
              Generate a personal access token, then paste it into the config above where it says{' '}
              <code>&lt;your-token&gt;</code>. Same security as OAuth, just a different UX.
            </p>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => window.location.assign('/me/settings/api-tokens')}
            >
              Generate a token →
            </Button>
          </div>
        </section>
      )}

      {/* Whether it works, as Toado saw it (TOADO-974): a read of one project, nothing more. */}
      <section className={styles.step} data-tour-anchor="mcp.check">
        <div className={styles.stepNum}>{nextStep}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3 className={styles.stepTitle}>Check it works</h3>
          {!currentCompanyId
            ? <p style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Join or make a workspace first: the check reads one of its projects.</p>
            : !projectsQuery.isSuccess
              ? <p style={{ color: 'var(--text-secondary)', fontSize: 13 }}>{projectsQuery.isError ? 'The projects could not be loaded. Reload the page to try again.' : 'Loading the projects…'}</p>
              : <AgentConnectionCheck projects={projects} />}
        </div>
      </section>

      <section className={styles.step} data-tour-anchor="mcp.troubleshooting">
        <div className={styles.stepNum}>?</div>
        <div style={{ flex: 1 }}>
          <h3 className={styles.stepTitle}>Troubleshooting</h3>
          <ul style={{ color: 'var(--text-secondary)', fontSize: 14, paddingLeft: 18, lineHeight: 1.7 }}>
            <li>
              <strong>"insufficient_scope"</strong> → the action requires a scope you didn't grant.
              Re-auth and approve the missing scope (Claude Code: re-run <code>claude mcp add</code>;
              PAT clients: regenerate the token with the broader scope selected).
            </li>
            <li>
              <strong>"Unauthorized"</strong> → token revoked or OAuth session expired. Re-auth.
            </li>
            <li>
              <strong>Can't find tickets?</strong> The token might be scoped to a different
              company/project than where the tickets live.
            </li>
            <li>
              <strong>Client says "no tools available"?</strong> Restart the client after editing
              its config file. Most clients only read MCP config at startup.
            </li>
            <li>
              <strong>Can't find toado in <code>/mcp</code>?</strong> The server was probably
              added in the default local scope (project-specific). Re-add it with{' '}
              <code>--scope user</code> to make it available across all projects.
            </li>
          </ul>
        </div>
      </section>
    </>
  );
}

/** Full-page wrapper. Thin shell around McpSetupContent so the same
 *  content can also render inside a modal without the page chrome. */
export function McpSetupPage() {
  // This tab's title (TOADO-905).
  const tabWorkspace = useTabWorkspaceName();
  useTabTitle({ parts: [tabWorkspace, "MCP connector"] });
  return (
    // Everything but a client's own panel is on every client's screen
    // (TOADO-701). The home page shows the content in a dialog without this
    // wrapper, and there the dialog holds it together the same way.
    <div className={styles.page} data-toado-view={EVERY_SCREEN}>
      <McpSetupContent />
    </div>
  );
}
